import { useEffect } from 'react';
import { usePortalAuthStore } from '@/store/portalAuthStore';

interface PortalAuthInitializerProps {
  children: React.ReactNode;
}

/**
 * Bundle K Phase K1D — initializes the portal auth store on app mount,
 * but ONLY when the current pathname is in the `/portal/*` namespace.
 *
 * Skipping the `/api/customer/me` probe on admin pages keeps the admin
 * SPA from sprinkling 401 noise into Sentry / browser network tabs.
 */
export function PortalAuthInitializer({ children }: PortalAuthInitializerProps) {
  const setCustomer = usePortalAuthStore((s) => s.setCustomer);
  const setLoading = usePortalAuthStore((s) => s.setLoading);
  const setInitialized = usePortalAuthStore((s) => s.setInitialized);

  useEffect(() => {
    const isPortalPath =
      typeof window !== 'undefined' && window.location.pathname.startsWith('/portal/');

    if (!isPortalPath) {
      setInitialized(true);
      return;
    }

    let cancelled = false;
    const probe = async () => {
      setLoading(true);
      try {
        // portalApi is a single object exporting every /portal/* endpoint, so
        // it can't be partially tree-shaken — a static import here would drag
        // all of it into the eager main-entry bundle even though this probe
        // only runs on /portal/* paths. Dynamic import keeps it out of every
        // admin operator's initial page load.
        const { default: portalApi } = await import('@/api/portalApi');
        const customer = await portalApi.me();
        if (!cancelled) {
          setCustomer(customer);
        }
      } catch {
        if (!cancelled) setCustomer(null);
      } finally {
        if (!cancelled) {
          setInitialized(true);
          setLoading(false);
        }
      }
    };

    probe();
    return () => {
      cancelled = true;
    };
  }, [setCustomer, setInitialized, setLoading]);

  return <>{children}</>;
}
